import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { ChecklistStatus } from '../../shared/checklistUtils';
import {
  getStatusLabel,
  isPositiveNumber,
  normalizeComparisonText,
  normalizeString,
  parseChecklistDate,
} from '../../shared/checklistUtils';

import type { BundledExperienceChecklistRow } from './types';

const BUNDLED_EXPERIENCES_FIELD = 'Bundled Experiences';

export function buildBundledExperienceRows({
  dealCurrency,
  hasOpportunitySourceError,
  hotelBlackoutDates,
  hotelTravelFrom,
  hotelTravelTo,
  relatedDeals,
}: {
  dealCurrency?: string;
  hasOpportunitySourceError?: boolean;
  hotelBlackoutDates?: Array<{ from__c?: string; name?: string; to__c?: string }>;
  hotelTravelFrom?: string;
  hotelTravelTo?: string;
  relatedDeals?: Array<OpportunityRelatedDeal>;
}): Array<BundledExperienceChecklistRow> {
  const bundledDeals = (relatedDeals ?? []).filter(isBundledExperienceDeal);
  const groupedDeals = new Map<string, Array<OpportunityRelatedDeal>>();

  for (const deal of bundledDeals) {
    const key = buildBundledExperienceKey(deal);
    groupedDeals.set(key, [...(groupedDeals.get(key) ?? []), deal]);
  }

  return Array.from(groupedDeals.entries()).map(([key, deals]) => {
    const sourceDeal = deals[0];
    const bundledExperience = normalizeString(sourceDeal.bundled_experiences__c) ?? sourceDeal.name ?? key;
    const linkedDealOptions = deals.map((deal) => normalizeString(deal.name) ?? String(deal.id));
    const currency = normalizeString(sourceDeal.currencyisocode);
    const rowDealCurrency = dealCurrency ?? currency;
    const blackoutDateRange = normalizeString(sourceDeal.blackout_dates__c);
    const childAge =
      normalizeString(sourceDeal.child_age_permitted__c) ?? normalizeString(sourceDeal.channel_fare_type__c);
    const validityOk = isBundledValidityOk({
      hotelTravelFrom,
      hotelTravelTo,
      travelFrom: sourceDeal.travel_start_date__c,
      travelTo: sourceDeal.travel_end_date__c,
    });
    const blackoutsOk =
      !hasOpportunitySourceError &&
      isBundledBlackoutOk({
        blackoutDateRange,
        hotelBlackoutDates,
      });
    const currencyOk = !!currency && !!rowDealCurrency && currency === rowDealCurrency;
    const childAgeOk = isBundledChildAgeOk(childAge);
    const status: ChecklistStatus =
      validityOk && blackoutsOk && currencyOk && childAgeOk ? 'approved' : 'requires approval';

    return {
      blackoutDateRange,
      blackoutsOk,
      bundledExperience,
      childAge,
      childAgeOk,
      currency,
      currencyOk,
      dealCurrency: rowDealCurrency,
      field: BUNDLED_EXPERIENCES_FIELD,
      id: buildBundledExperienceRowId(deals),
      linkedDealOptions,
      status,
      statusLabel: getStatusLabel(status),
      travelFrom: sourceDeal.travel_start_date__c ?? undefined,
      travelTo: sourceDeal.travel_end_date__c ?? undefined,
      validityOk,
    };
  });
}
function isBundledExperienceDeal(deal: OpportunityRelatedDeal) {
  return (
    !!normalizeString(deal.bundled_experiences__c) || isPositiveNumber(Number(deal.number_of_bundled_experience__c))
  );
}

function buildBundledExperienceKey(deal: OpportunityRelatedDeal) {
  return normalizeString(deal.bundled_experiences__c) ?? String(deal.id);
}

function buildBundledExperienceRowId(deals: Array<OpportunityRelatedDeal>) {
  return `experience:${deals.map(getDealSourceId).sort().join(':')}`;
}

function getDealSourceId(deal: OpportunityRelatedDeal) {
  return `deal_offer__c:${normalizeString(deal.sfid)?.toLowerCase() ?? String(deal.id).toLowerCase()}`;
}

export function isBundledValidityOk({
  hotelTravelFrom,
  hotelTravelTo,
  travelFrom,
  travelTo,
}: {
  hotelTravelFrom?: string;
  hotelTravelTo?: string;
  travelFrom?: string | null;
  travelTo?: string | null;
}) {
  const bundledFrom = parseChecklistDate(travelFrom);
  const bundledTo = parseChecklistDate(travelTo);
  const hotelFrom = parseChecklistDate(hotelTravelFrom);
  const hotelTo = parseChecklistDate(hotelTravelTo);

  if (!bundledFrom?.isValid() || !bundledTo?.isValid() || !hotelTo?.isValid()) {
    return false;
  }

  const startsInsideTravel = hotelFrom?.isValid() ? !bundledFrom.isBefore(hotelFrom, 'day') : true;
  return startsInsideTravel && !bundledTo.isAfter(hotelTo, 'day');
}

export function isBundledBlackoutOk({
  blackoutDateRange,
  hotelBlackoutDates,
}: {
  blackoutDateRange?: string;
  hotelBlackoutDates?: Array<{ from__c?: string; name?: string; to__c?: string }>;
}) {
  if (!blackoutDateRange || blackoutDateRange.toLowerCase() === 'none') {
    return true;
  }

  const normalizedBundledBlackoutEntries = normalizeBlackoutEntries(blackoutDateRange);
  const normalizedHotelBlackoutEntries = (hotelBlackoutDates ?? []).map((blackout) =>
    normalizeComparisonText([blackout.name, blackout.from__c, blackout.to__c].filter(Boolean).join(' ')),
  );

  return (
    normalizedBundledBlackoutEntries.length > 0 &&
    normalizedHotelBlackoutEntries.length > 0 &&
    normalizedBundledBlackoutEntries.every((bundledBlackout) =>
      normalizedHotelBlackoutEntries.some((hotelBlackout) => hotelBlackout.includes(bundledBlackout)),
    )
  );
}

function normalizeBlackoutEntries(value: string) {
  return value
    .split(';')
    .map((part) => normalizeComparisonText(part))
    .filter(Boolean);
}

export function isBundledChildAgeOk(childAge?: string) {
  if (!childAge || !/\b(child|children|kid|kids)\b/i.test(childAge)) {
    return true;
  }

  return false;
}
